import { isValidColour, lighten, darken, desaturate, toHex } from '@utils/colour';

import iconfont from '@plugins/vuetify/iconfont';
import icons from '@plugins/vuetify/icons';

import config from '@/config';

/**
 * Creates a Vuetify theme colour object
 * @param {String} hex
 * @returns Object
 */
export const createColoursFromHex = (hex, override = {}) => {
    if (!isValidColour(hex)) {
        return false;
    }

    const colours = {
        base: toHex(hex),
        lighten1: toHex(lighten(hex, 0.05)),
        lighten2: toHex(lighten(hex, 0.1)),
        lighten3: toHex(lighten(hex, 0.15)),
        lighten4: toHex(lighten(desaturate(hex, 0.55), 0.25)),
        lighten5: toHex(lighten(desaturate(hex, 0.55), 0.35)),
        darken1: toHex(darken(hex, 0.2)),
        darken2: toHex(darken(hex, 0.3)),
        darken3: toHex(darken(hex, 0.4)),
        darken4: toHex(darken(hex, 0.47)),
        darken5: toHex(darken(hex, 0.55))
    };

    return { ...colours, ...override };
};

/**
 * Creates Vuetify themes from a hex
 * @param {Object} colours
 * @returns Object
 */
export const createThemeFromBranding = colours => {
    return {
        light: {
            primaryBackground: {
                base: colours.primaryBackground
            },
            primaryText: {
                base: colours.primaryText
            },
            primaryLight: {
                base: colours.primaryLight
            },
            special: {
                base: '#5d0a7e'
            },
            //
            anchor: '#006fa5',
            background: {
                base: '#ffffff',
                lighten5: '#ffffff',
                lighten4: '#ffffff',
                lighten3: '#ffffff',
                lighten2: '#ffffff',
                lighten1: '#ffffff',
                darken: '#f9f9f9',
                darken1: '#f9f9f9',
                darken2: '#f5f5f5',
                darken3: '#eeeeee',
                darken4: '#dddddd'
            },
            error: {
                base: '#ff5252',
                lighten6: '#f3eaea',
                lighten5: '#ffe0e0',
                lighten4: '#ffc6b9',
                lighten3: '#ffa99e',
                lighten2: '#ff8c84',
                lighten1: '#ff6f6a',
                darken1: '#df323b',
                darken2: '#bf0025',
                darken3: '#9f0010',
                darken4: '#800000'
            },
            info: {
                base: '#2196f3',
                lighten5: '#d9edfd',
                lighten4: '#b5ffff',
                lighten3: '#95e8ff',
                lighten2: '#75ccff',
                lighten1: '#51b0ff',
                darken1: '#007cd6',
                darken2: '#0064ba',
                darken3: '#004d9f',
                darken4: '#003784'
            },
            success: createColoursFromHex('#39b985', {
                lighten5: '#e6f6ef',
                lighten6: '#d8f1e7'
            }),
            warning: {
                base: '#fb8c00',
                lighten5: '#ffe9e9',
                lighten4: '#fffb82',
                lighten3: '#ffdf67',
                lighten2: '#ffc24b',
                lighten1: '#ffa72d',
                darken1: '#db7200',
                darken2: '#bb5900',
                darken3: '#9d4000',
                darken4: '#802700'
            }
        }
    };
};

/**
 * Sets the Vuetify theme
 * @param {Object} colours
 */
export const applyThemeFromBranding = colours => {
    const themes = createThemeFromBranding(colours);

    if (themes && config.ENV !== 'test') {
        window.Vue.$vuetify.theme.options.customProperties = true;
        window.Vue.$vuetify.theme.themes = themes;
        window.Vue.$vuetify.theme.applyTheme();
        window.Vue.$vuetify.icons.iconfont = iconfont;
        window.Vue.$vuetify.icons.values = icons;
    }
};
